import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  User, 
  ArrowLeft, 
  Calendar,
  Mail,
  Shield,
  Zap,
  Crown,
  Clock,
  Target,
  Award,
  Settings,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Edit2,
  Save,
  X
} from 'lucide-react'
import { authService, AuthUser } from '@/services/auth'
import { UserLimits } from '@/types'
import PromptLimitIndicator from '@/components/PromptLimitIndicator'
import { useTranslation } from 'react-i18next'

export default function ProfilePage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [userLimits, setUserLimits] = useState<UserLimits | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditingUsername, setIsEditingUsername] = useState(false)
  const [usernameValue, setUsernameValue] = useState('')
  const [savingUsername, setSavingUsername] = useState(false)
  const [usernameError, setUsernameError] = useState<string | null>(null)

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const user = await authService.getCurrentUser()
        if (!user) {
          navigate('/login')
          return
        }

        setCurrentUser(user)
        setUsernameValue(user.username || '')
        
        const limits = await authService.getUserLimits()
        setUserLimits(limits)
      } catch (err) {
        console.error('Error loading user data:', err)
        setError('Kullanıcı bilgileri yüklenirken hata oluştu')
      } finally {
        setLoading(false)
      }
    }

    loadUserData()
  }, [navigate])

  const handleEditUsername = () => {
    setIsEditingUsername(true)
    setUsernameValue(currentUser?.username || '')
    setUsernameError(null)
  }

  const handleCancelEdit = () => {
    setIsEditingUsername(false)
    setUsernameValue(currentUser?.username || '')
    setUsernameError(null)
  }

  const handleSaveUsername = async () => {
    if (!currentUser) return

    setUsernameError(null)
    setSavingUsername(true)

    try {
      const updatedUser = await authService.updateUsername(usernameValue)
      setCurrentUser(updatedUser)
      setIsEditingUsername(false)
    } catch (err: any) {
      setUsernameError(err.message || 'Kullanıcı adı güncellenirken hata oluştu')
    } finally {
      setSavingUsername(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-white/20 border-t-violet-400 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-2 border-purple-400/30 rounded-full animate-pulse mx-auto"></div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-white">{t('profile.loadingTitle')}</h3>
            <p className="text-white/70">{t('profile.loadingSubtitle')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="card-gradient max-w-md mx-auto border-red-400/50 bg-red-500/10">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-4">{t('profile.errorTitle')}</h2>
            <p className="text-white/80 mb-6">{error}</p>
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary"
            >
              {t('profile.backHome')}
            </motion.button>
          </div>
        </div>
      </div>
    )
  }

  if (!currentUser) return null

  const displayName = currentUser.username || currentUser.email?.split('@')[0] || t('userDropdown.fallbackUser')
  const remainingPrompts = userLimits ? userLimits.maxPrompts - userLimits.promptCount : 0
  const resetDate = userLimits ? new Date(userLimits.resetDate) : new Date()
  const daysUntilReset = Math.ceil((resetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
  const dateLocale = i18n.language?.startsWith('en') ? 'en-US' : i18n.language?.startsWith('tr') ? 'tr-TR' : i18n.language

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        {/* Mobile: back (left), title (center), settings (right) */}
        <div className="sm:hidden px-4 mb-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <motion.button
                onClick={() => navigate('/')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('profile.home')}</span>
              </motion.button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-xl font-bold text-gradient">{t('profile.header')}</h1>
                <p className="text-white/60 text-xs">{t('profile.subtitle')}</p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/settings"
                className="btn-secondary flex items-center space-x-2"
              >
                <Settings className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop layout unchanged */}
        <div className="hidden sm:flex items-center justify-between px-4">
          <motion.button
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{t('profile.home')}</span>
          </motion.button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gradient">{t('profile.header')}</h1>
            <p className="text-white/60 text-base">{t('profile.subtitle')}</p>
          </div>
          <Link
            to="/settings"
            className="btn-secondary flex items-center space-x-2"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">{t('settings.theme')}</span>
          </Link>
        </div>
      </motion.div>

      {/* Profile Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card-gradient mx-4"
      >
        <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
          {/* Avatar */}
          <div className="relative">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <User className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white/20">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          </div>

          {/* User Info */}
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-2xl font-bold text-white mb-1">{displayName}</h2>
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 text-white/70">
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span className="text-sm">{currentUser.email}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span className="text-sm">{t('profile.verified')}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Prompt Limits Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4 mx-4"
      >
        <h3 className="text-xl font-bold text-white flex items-center space-x-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          <span>{t('profile.promptLimits')}</span>
        </h3>

        {userLimits && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Limit Overview */}
            <div className="card-gradient">
              <PromptLimitIndicator 
                userLimits={userLimits} 
                showDetails={true}
                className="bg-transparent border-none p-3"
              />
            </div>

            {/* Detailed Stats */}
            <div className="card-gradient space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">{t('profile.details')}</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-white/5 rounded-xl">
                  <Target className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{userLimits.promptCount}</div>
                  <div className="text-xs text-white/60">{t('profile.used')}</div>
                </div>

                <div className="text-center p-3 bg-white/5 rounded-xl">
                  <Award className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{remainingPrompts}</div>
                  <div className="text-xs text-white/60">{t('profile.remaining')}</div>
                </div>

                <div className="text-center p-3 bg-white/5 rounded-xl">
                  <Crown className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{userLimits.maxPrompts}</div>
                  <div className="text-xs text-white/60">{t('profile.total')}</div>
                </div>

                <div className="text-center p-3 bg-white/5 rounded-xl">
                  <Clock className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{daysUntilReset}</div>
                  <div className="text-xs text-white/60">{t('profile.daysLeft')}</div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <div className="flex items-center space-x-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-300 font-medium text-sm">{t('profile.resetDate')}</span>
                </div>
                <p className="text-blue-200 text-sm">
                  {resetDate.toLocaleDateString(dateLocale || undefined, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Account Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-4 mx-4"
      >
        <h3 className="text-xl font-bold text-white flex items-center space-x-2">
          <User className="w-5 h-5 text-violet-400" />
          <span>{t('profile.accountInfo')}</span>
        </h3>

        <div className="card-gradient space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-white/70 text-sm font-medium">{t('profile.username')}</label>
                {!isEditingUsername && (
                  <motion.button
                    onClick={handleEditUsername}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-violet-400 hover:text-violet-300 transition-colors"
                    title={t('profile.editUsername')}
                  >
                    <Edit2 className="w-4 h-4" />
                  </motion.button>
                )}
              </div>
              {isEditingUsername ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={usernameValue}
                    onChange={(e) => {
                      setUsernameValue(e.target.value)
                      setUsernameError(null)
                    }}
                    className="w-full p-3 bg-white/5 rounded-xl border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder={t('auth.usernamePlaceholder')}
                    disabled={savingUsername}
                    maxLength={30}
                  />
                  {usernameError && (
                    <p className="text-red-400 text-xs">{usernameError}</p>
                  )}
                  <div className="flex items-center space-x-2">
                    <motion.button
                      onClick={handleSaveUsername}
                      disabled={savingUsername || !usernameValue.trim()}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center space-x-2 px-4 py-2 bg-violet-500 hover:bg-violet-600 disabled:bg-white/10 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
                    >
                      <Save className="w-4 h-4" />
                      <span>{savingUsername ? t('profile.saving') : t('profile.save')}</span>
                    </motion.button>
                    <motion.button
                      onClick={handleCancelEdit}
                      disabled={savingUsername}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 disabled:bg-white/5 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
                    >
                      <X className="w-4 h-4" />
                      <span>{t('profile.cancel')}</span>
                    </motion.button>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-white">{currentUser.username || t('profile.unknown')}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-white/70 text-sm font-medium">{t('profile.email')}</label>
              <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                <span className="text-white">{currentUser.email}</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-white/70 text-sm font-medium">{t('profile.status')}</label>
              <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-300">{t('profile.active')}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-white/70 text-sm font-medium">{t('profile.createdAt')}</label>
              <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-white/60" />
                  <span className="text-white">
                    {userLimits ? new Date(userLimits.createdAt).toLocaleDateString(dateLocale || undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : t('profile.unknown')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mx-4"
      >
        <h3 className="text-xl font-bold text-white mb-4">{t('profile.quickActions')}</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/"
            className="card-gradient hover:bg-white/15 transition-colors group"
          >
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto group-hover:bg-blue-500/30 transition-colors">
                <Target className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">{t('profile.newGame')}</h4>
                <p className="text-white/60 text-sm">{t('profile.newGameDesc')}</p>
              </div>
            </div>
          </Link>

          <Link
            to="/settings"
            className="card-gradient hover:bg-white/15 transition-colors group"
          >
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto group-hover:bg-purple-500/30 transition-colors">
                <Settings className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">{t('profile.settings')}</h4>
                <p className="text-white/60 text-sm">{t('profile.settingsDesc')}</p>
              </div>
            </div>
          </Link>

          <div className="card-gradient opacity-60">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center mx-auto">
                <Crown className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">{t('profile.premium')}</h4>
                <p className="text-white/60 text-sm">{t('profile.comingSoon')}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
